import * as Sentry from "@sentry/react";
import { useState } from "react";
import { toast } from "react-toastify";

import { Button, Form, Input, Modal } from "antd";

import { useUser } from "hooks";

import { buildWebToCaseParams, salesforceConfig } from "./salesforce";
import { useSupportTicketStore } from "./useSupportTicketStore";

const { TextArea } = Input;

// Salesforce Case Description supports up to 32,000 characters.
const DESCRIPTION_MAX_LENGTH = 32000;

interface SupportTicketFormValues {
  subject: string;
  description: string;
  name?: string;
  email: string;
  phone?: string;
  company?: string;
}

function SupportTicketModal() {
  const { user } = useUser();
  const isOpen = useSupportTicketStore((s) => s.isOpen);
  const close = useSupportTicketStore((s) => s.close);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    form.resetFields();
    close();
  };

  const onFinish = (values: SupportTicketFormValues) => {
    if (!salesforceConfig.actionUrl) {
      toast.error("Support is not configured. Please contact your administrator.");
      return;
    }

    // retURL only matters for Salesforce's post-submit redirect, which our
    // fire-and-forget fetch never follows. Send origin + pathname (no query
    // string) so URL parameters are not leaked to Salesforce.
    const retUrl = window.location.origin + window.location.pathname;
    const params = buildWebToCaseParams(
      {
        subject: values.subject.trim(),
        description: values.description.trim(),
        name: (values.name ?? "").trim(),
        email: values.email.trim(),
        phone: (values.phone ?? "").trim(),
        company: (values.company ?? "").trim()
      },
      retUrl
    );

    setSubmitting(true);
    fetch(salesforceConfig.actionUrl, {
      method: "POST",
      body: params,
      mode: "no-cors"
    })
      .then(() => {
        toast.success("Support ticket submitted");
        handleClose();
      })
      .catch((error) => {
        Sentry.withScope((scope) => {
          scope.setLevel("error");
          Sentry.captureException(error);
        });
        toast.error("Failed to submit support ticket. Please try again.");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal
      title="Open Support Ticket"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      // Not `data-testid` directly on `Modal`: rc-dialog forwards data-*
      // attributes only to the outer `.ant-modal-root`, which is
      // `position: fixed` with zero height — present in the DOM but never
      // "rendered" to anything measuring it. `wrapProps` puts it on
      // `.ant-modal-wrap`, the viewport-covering box that actually has a size.
      wrapProps={{ "data-testid": "support-ticket-modal" }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          product: salesforceConfig.product,
          company: user?.organization?.name ?? "",
          name: user ? `${user.firstName} ${user.lastName}` : "",
          email: user?.email ?? "",
          phone: user?.phoneNumber ?? ""
        }}>
        <Form.Item label="Product" name="product">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="Subject"
          name="subject"
          rules={[{ required: true, whitespace: true, message: "Subject is required" }]}>
          <Input maxLength={80} autoFocus />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Description is required"
            }
          ]}>
          <TextArea rows={3} maxLength={DESCRIPTION_MAX_LENGTH} />
        </Form.Item>
        <Form.Item label="Company" name="company">
          <Input disabled maxLength={80} />
        </Form.Item>
        <Form.Item label="Contact Name" name="name">
          <Input maxLength={80} />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Invalid email format" }
          ]}>
          <Input maxLength={80} />
        </Form.Item>
        <Form.Item label="Phone" name="phone">
          <Input maxLength={40} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Button onClick={handleClose} style={{ marginRight: 8 }} disabled={submitting}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default SupportTicketModal;
